import axios from 'axios';
import { getDelivererApiUrl } from '../../../services/env.service';
import {
  CartUpsertRequest,
  CartUpsertResponse,
  BezorgmomentenResponse,
  GetRouteDeclarationFormInformationResponse,
  PagedSalesOrderResponse,
  ProductInformationResponse,
  WeeklyMenuResponse,
  ChangeQuantityRequest,
  AddItemToSalesOrderDeliveryRequest,
  ProductAvailabilityRequest,
  ProductAvailabilityResponse,
  SalesOrderResponse,
  UpdateCustomerInformationRequest,
  ActiveWeeklyMenuResponse
} from './types';

const delivererApiUrl = getDelivererApiUrl() + '/api';

class DelivererClient {
  getRouteDeclarationFormInformation() {
    return new Promise<GetRouteDeclarationFormInformationResponse>((resolve, reject) => {
      const url = `${delivererApiUrl}/routes/declaration-form-information`;
      axios
        .get<GetRouteDeclarationFormInformationResponse>(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getBezorgmomentenPagedResponse(postcode: string, klantnummer: string, endDate: Date) {
    return new Promise<BezorgmomentenResponse>((resolve, reject) => {
      const startDateTime = new Date().toISOString();
      const endDateTime = endDate.toISOString();
      const url = `${delivererApiUrl}/v1/Bezorgmoment/${postcode}/${klantnummer}?startDateTime=${encodeURIComponent(
        startDateTime
      )}&endDateTime=${encodeURIComponent(endDateTime)}`;
      
      axios
        .get<BezorgmomentenResponse>(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getProductInformation(productId: string) {
    return new Promise<ProductInformationResponse>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/catalog-management/product-information/${productId}`;
      axios
        .get<ProductInformationResponse>(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((axiosError) => {
          reject(axiosError.message);
        });
    });
  }

  getWeeklyMenu(weeklyMenuKey: string) {
    return new Promise<WeeklyMenuResponse>((resolve, reject) => {
      const salesChannel = 'bezorgers';
      const url = `${delivererApiUrl}/v2/catalog-management/weekly-menu/${salesChannel}/${weeklyMenuKey}`;
      axios
        .get<WeeklyMenuResponse>(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getActiveWeeklyMenus() {
    return new Promise<ActiveWeeklyMenuResponse>((resolve, reject) => {
      const salesChannel = 'bezorgers';
      const url = `${delivererApiUrl}/v2/catalog-management/weekly-menu/${salesChannel}/active-weekly-menus`;
      axios
        .get<ActiveWeeklyMenuResponse>(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getOrderBySalesOrderId(salesOrderId: string) {
    return new Promise<SalesOrderResponse>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/order-management/${salesOrderId}`;
      axios
        .get<SalesOrderResponse>(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getOrdersByCustomer(
    customerNumber: string,
    pageNumber: number | undefined,
    pageSize: number | undefined
  ) {
    return new Promise<PagedSalesOrderResponse>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/order-management/by-customer/${customerNumber}?pageNumber=${pageNumber}&pageSize=${pageSize}&orderOn=requestedDeliveryMoment`;

      axios
        .get<PagedSalesOrderResponse>(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((axiosError) => {
          reject(axiosError.message);
        });
    });
  }

  upsertCart(cartData: CartUpsertRequest) {
    return new Promise<CartUpsertResponse>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/pointofsales/carts/create-or-update`;
      axios
        .post<CartUpsertResponse>(url, cartData, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  checkoutCart(cartId: string) {
    return new Promise<void>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/pointofsales/carts/${cartId}/checkout`;
      axios
        .post<void>(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  createPaymentForCart(cartId: string, paymentMethod: string) {
    return new Promise<void>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/pointofsales/payments/create-for-cart/${cartId}`;
      const cartData = {
        externalPaymentMethodId: paymentMethod,
        redirectUrl: '', // needed for payment methods with Mollie, not used for direct debit so we set it to empty
        cancelUrl: null
      };
      axios
        .post<void>(url, cartData)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  cancelSalesOrderDelivery(salesOrderId: string, deliveryId: string) {
    return new Promise<void>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/order-management/${salesOrderId}/sales-order-delivery/${deliveryId}/cancel`;
      axios
        .post(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  cancelSalesOrderItem(salesOrderId: string, salesOrderItemId: string) {
    return new Promise<void>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/order-management/${salesOrderId}/sales-order-item/${salesOrderItemId}/cancel`;
      axios
        .post(url)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  changeSalesOrderItemQuantity(
    salesOrderId: string,
    salesOrderItemId: string,
    changeQuantityRequest: ChangeQuantityRequest,
    correlationId?: string
  ) {
    return new Promise<{ correlationId: string }>((resolve, reject) => {
      let url = `${delivererApiUrl}/v1/order-management/${salesOrderId}/sales-order-item/${salesOrderItemId}/change-quantity`;

      if (correlationId != null) {
        // checks for both null and undefined
        url += `?correlationId=${encodeURIComponent(correlationId)}`;
      }

      axios
        .post(url, changeQuantityRequest)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  addItemsToSalesOrderDelivery(
    salesOrderId: string,
    deliveryId: string,
    addItemToSalesOrderDeliveryRequest: AddItemToSalesOrderDeliveryRequest
  ) {
    return new Promise<void>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/order-management/${salesOrderId}/sales-order-delivery/${deliveryId}/add-items`;
      axios
        .post(url, addItemToSalesOrderDeliveryRequest)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  getStockCheckAvailability(deliveryDate: string, products: ProductAvailabilityRequest) {
    return new Promise<ProductAvailabilityResponse>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/stock-management/product-availability/${deliveryDate}/check-availability`;
      axios
        .post<ProductAvailabilityResponse>(url, products)
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }

  updateCustomerInformation(
    customerNumber: string,
    idempotencyKey: string,
    information: UpdateCustomerInformationRequest
  ) {
    return new Promise<void>((resolve, reject) => {
      const url = `${delivererApiUrl}/v1/customers/${customerNumber}/update-information`;
      axios
        .put<void>(url, information, {
          headers: { 'x-idempotency-key': idempotencyKey }
        })
        .then((response) => resolve(response.data))
        .catch((axiosError) => reject(axiosError.message));
    });
  }
}

const delivererClient = new DelivererClient();

export { delivererClient };
